import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { HOURS_IN_DAY, MINUTES_IN_HOUR } from '@stdlib/constants-time';
import {
  Button,
  Checkbox,
  ConfigProvider,
  Divider,
  Form,
  InputNumber,
  Popconfirm,
  Radio,
} from 'antd';
import type { CheckboxValueType } from 'antd/es/checkbox/Group';
import generatePicker from 'antd/es/date-picker/generatePicker';
import frFR from 'antd/es/locale/fr_FR';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import 'dayjs/locale/fr';

import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import type { GenerateConfig } from 'rc-picker/es/generate';
import dayjsGenerateConfig from 'rc-picker/es/generate/dayjs';

import { DAYS_OPTIONS } from 'app/typings/core/extranet/constants/days';
import { FREQUENCY_MODE } from 'app/typings/core/extranet/inventory/enums/FrequencyMode';
import type { FrequencyMode } from 'app/typings/core/extranet/inventory/enums/FrequencyMode';
import { pluralize } from 'app/utils/strings';

import { useTransientFlag } from '../../hooks/useTransientFlag';
import { getIsoWeekday } from '../../utils/weekdays';

import styles from './AvailabilityForm.module.scss';
import type {
  AvailabilityFormValues,
  AvailabilityInitialValues,
} from './availabilityForm.types';
import { useAvailabilityForm } from './useAvailabilityForm';

dayjs.extend(weekday);
dayjs.extend(localeData);

const DatePicker = generatePicker<Dayjs>(
  dayjsGenerateConfig as unknown as GenerateConfig<Dayjs>
);

type Props = {
  title: string;
  subtitle?: string;
  initialValues: AvailabilityInitialValues;
  durationMinutes?: number;
  onSubmit: (values: AvailabilityFormValues) => void;
  onClose: () => void;
  onDelete?: () => void;
  onChange?: (values: AvailabilityFormValues | null) => void;
  isWithinOpeningHours?: (start: Dayjs) => boolean;
  isDayClosed?: (date: Dayjs) => boolean;
  isEditPopover?: boolean;
};

const FREQUENCY_OPTIONS: { label: string; value: FrequencyMode }[] = [
  { label: 'No repeat', value: FREQUENCY_MODE.NONE },
  { label: 'Every day', value: FREQUENCY_MODE.DAILY },
  { label: 'Every week', value: FREQUENCY_MODE.WEEKLY },
  { label: 'Custom', value: FREQUENCY_MODE.CUSTOM },
];

const getBodyPopupContainer = () => document.body;

const TIME_DROPDOWN_CLASS = 'availabilityTimeDropdown';

const TIME_FORMATS = ['HH:mm', 'H:mm', 'HH:m', 'H:m', 'HH:', 'H:', 'HH', 'H'];

const scrollTimeColumnsToFirstEnabled = () => {
  const columns = document.querySelectorAll<HTMLElement>(
    `.${TIME_DROPDOWN_CLASS}:not(.ant-picker-dropdown-hidden) .ant-picker-time-panel-column`
  );

  columns.forEach((column) => {
    const firstEnabled = column.querySelector<HTMLElement>(
      '.ant-picker-time-panel-cell:not(.ant-picker-time-panel-cell-disabled)'
    );

    if (firstEnabled) {
      column.scrollTop = firstEnabled.offsetTop;
    }
  });
};

const handleTimeOpenChange = (open: boolean) => {
  if (!open) {
    return;
  }

  requestAnimationFrame(() =>
    requestAnimationFrame(scrollTimeColumnsToFirstEnabled)
  );
};

const disabledPastDate = (current: Dayjs) =>
  !!current && current < dayjs().startOf('day');

const buildDisabledPastTime = (selectedDate?: Dayjs) => () => {
  if (!selectedDate || !selectedDate.isSame(dayjs(), 'day')) {
    return {};
  }

  const currentHour = dayjs().hour();
  const currentMinute = dayjs().minute();

  const allMinutes = Array.from(
    { length: MINUTES_IN_HOUR },
    (_, minute) => minute
  );

  return {
    disabledHours: () =>
      Array.from({ length: HOURS_IN_DAY }, (_, hour) => hour).filter(
        (hour) => hour < currentHour
      ),
    disabledMinutes: (selectedHour: number) => {
      if (selectedHour < currentHour) {
        return allMinutes;
      }

      if (selectedHour === currentHour) {
        return allMinutes.filter((minute) => minute < currentMinute);
      }

      return [];
    },
  };
};

type WeekdaysFieldProps = {
  value?: number[];
  onChange?: (value: number[]) => void;
  lockedWeekday: number | null;
  onEmptyWithoutLock: () => void;
  onLockedRemovalAttempt: () => void;
};

const WeekdaysField = ({
  value = [],
  onChange,
  lockedWeekday,
  onEmptyWithoutLock,
  onLockedRemovalAttempt,
}: WeekdaysFieldProps) => {
  const handleChange = (next: CheckboxValueType[]) => {
    const nextDays = next as number[];

    if (
      lockedWeekday !== null &&
      value.includes(lockedWeekday) &&
      !nextDays.includes(lockedWeekday)
    ) {
      onLockedRemovalAttempt();

      return;
    }

    if (nextDays.length === 0 && lockedWeekday === null) {
      onEmptyWithoutLock();

      return;
    }

    onChange?.(nextDays);
  };

  return (
    <Checkbox.Group
      className={styles.weekdayCheckboxGroup}
      value={value}
      onChange={handleChange}
    >
      {DAYS_OPTIONS.map((option) => (
        <Checkbox
          key={option.value}
          value={option.value}
          className={
            option.value === lockedWeekday ? styles.lockedWeekday : undefined
          }
        >
          {option.label}
        </Checkbox>
      ))}
    </Checkbox.Group>
  );
};

export const AvailabilityForm = ({
  title,
  subtitle,
  initialValues,
  durationMinutes,
  onSubmit,
  onClose,
  onDelete,
  onChange,
  isWithinOpeningHours,
  isDayClosed,
  isEditPopover,
}: Props) => {
  const [showLockedDayNote, flashLockedDayNote] = useTransientFlag();
  const [showDaysUpdatedNote, flashDaysUpdatedNote] = useTransientFlag();

  const {
    form,
    formInitialValues,
    isRecurringLocked,
    normalizeTimeRange,
    handleValuesChange,
    handleSave,
    switchToNoRepeat,
    timeWindowError,
  } = useAvailabilityForm({
    initialValues,
    onSubmit,
    onChange,
    durationMinutes,
    onWeekdaysSynced: flashDaysUpdatedNote,
  });

  const unitValues = Form.useWatch('capacity', form) as number | undefined;
  const selectedDate = Form.useWatch('date', form) as Dayjs | undefined;
  const timeRange = Form.useWatch('timeRange', form) as
    [Dayjs, Dayjs | null] | undefined;
  const disabledPastTime = buildDisabledPastTime(selectedDate);

  const dateError = timeWindowError && !selectedDate;
  const timeError = timeWindowError && !timeRange?.[0];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>{title}</div>
          {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </div>
        <button type="button" className={styles.closeBtn} onClick={onClose}>
          <CloseOutlined />
        </button>
      </div>

      <Divider className={styles.divider} />

      <ConfigProvider locale={frFR}>
        <Form
          form={form}
          name="availabilityForm"
          layout="vertical"
          size="small"
          initialValues={formInitialValues}
          className={styles.form}
          onValuesChange={handleValuesChange}
        >
          <div className={styles.formSection}>
            <div className={styles.sectionLabel}>Time window</div>
            <div className={styles.timeRow}>
              <Form.Item name="date" rules={[{ required: true }]} noStyle>
                <DatePicker
                  format="DD MMM YYYY"
                  allowClear={false}
                  getPopupContainer={getBodyPopupContainer}
                  className={styles.datePicker}
                  disabledDate={disabledPastDate}
                  disabled={isRecurringLocked}
                  status={dateError ? 'error' : undefined}
                  placeholder="Select date"
                />
              </Form.Item>
              <Form.Item
                name="timeRange"
                rules={[{ required: true }]}
                normalize={normalizeTimeRange}
                noStyle
              >
                <DatePicker.RangePicker
                  picker="time"
                  format={TIME_FORMATS}
                  allowClear={false}
                  allowEmpty={[false, true]}
                  placeholder={['00:00', '00:00']}
                  minuteStep={15}
                  className={styles.timeRangePicker}
                  getPopupContainer={getBodyPopupContainer}
                  dropdownClassName={TIME_DROPDOWN_CLASS}
                  onOpenChange={handleTimeOpenChange}
                  disabled={[false, true]}
                  disabledTime={disabledPastTime}
                  status={timeError ? 'error' : undefined}
                />
              </Form.Item>
            </div>
            {timeWindowError && (
              <div className={styles.timeWindowError}>
                Time window is required to save
              </div>
            )}
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) =>
                prev.date !== cur.date || prev.timeRange !== cur.timeRange
              }
            >
              {({ getFieldValue }) => {
                if (!isWithinOpeningHours) {
                  return null;
                }

                const date = getFieldValue('date') as Dayjs | undefined;
                const range = getFieldValue('timeRange') as
                  [Dayjs, Dayjs | null] | undefined;
                const start = range?.[0];

                if (!date || !start) {
                  return null;
                }

                if (isDayClosed?.(date)) {
                  return null;
                }

                const startDateTime = date
                  .hour(start.hour())
                  .minute(start.minute());

                if (isWithinOpeningHours(startDateTime)) {
                  return null;
                }

                return (
                  <div className={styles.openingHoursNote}>
                    This slot falls outside opening hours. It will still be
                    bookable.
                  </div>
                );
              }}
            </Form.Item>
          </div>
          <div className={styles.formSection}>
            <div className={styles.sectionLabel}>Capacity per slot</div>
            <div className={styles.capacityRow}>
              <Form.Item name="capacity" rules={[{ required: true }]} noStyle>
                <InputNumber
                  min={isEditPopover ? 0 : 1}
                  className={styles.capacityInput}
                />
              </Form.Item>
              <span className={styles.capacityLabel}>
                {pluralize(unitValues, 'unit', 'units', false)}
              </span>
            </div>
          </div>
          <div className={styles.formSection}>
            <div className={styles.sectionLabel}>Frequency</div>
            <Form.Item name="frequency" noStyle>
              <Radio.Group
                name="frequency"
                className={styles.frequencyRadioGroup}
              >
                {FREQUENCY_OPTIONS.map((option) => (
                  <Radio.Button
                    key={option.value}
                    value={option.value}
                    disabled={
                      option.value === FREQUENCY_MODE.NONE && isRecurringLocked
                    }
                  >
                    {option.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) =>
                prev.frequency !== cur.frequency || prev.date !== cur.date
              }
            >
              {({ getFieldValue }) => {
                const frequency = getFieldValue('frequency') as FrequencyMode;
                const date = getFieldValue('date') as Dayjs | undefined;
                const weekdayLabel = date ? date.format('dddd') : 'week';

                if (frequency === FREQUENCY_MODE.DAILY) {
                  return (
                    <div className={styles.frequencyNote}>
                      Repeats every <strong>day</strong>
                    </div>
                  );
                }

                if (frequency === FREQUENCY_MODE.WEEKLY) {
                  return (
                    <div className={styles.frequencyNote}>
                      Repeats every <strong>{weekdayLabel}</strong>
                    </div>
                  );
                }

                if (frequency === FREQUENCY_MODE.CUSTOM) {
                  return (
                    <div className={styles.weekdaySelector}>
                      <div className={styles.weekdaySelectorLabel}>
                        Repeats every :
                      </div>
                      <Form.Item name="weekdays" noStyle>
                        <WeekdaysField
                          lockedWeekday={date ? getIsoWeekday(date) : null}
                          onEmptyWithoutLock={switchToNoRepeat}
                          onLockedRemovalAttempt={flashLockedDayNote}
                        />
                      </Form.Item>
                      {showLockedDayNote && (
                        <div className={styles.weekdayLockNote}>
                          Custom frequency must include the slot's date
                        </div>
                      )}
                      {showDaysUpdatedNote && (
                        <div className={styles.dateSyncNote}>
                          Days updated to match the new date
                        </div>
                      )}
                    </div>
                  );
                }

                return null;
              }}
            </Form.Item>
          </div>
        </Form>
      </ConfigProvider>

      <Divider className={styles.divider} />

      <div className={styles.footer}>
        {onDelete ? (
          isRecurringLocked ? (
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={onDelete}
            >
              <DeleteOutlined />
              Delete
            </button>
          ) : (
            <Popconfirm
              title="Delete this availability?"
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              onConfirm={onDelete}
              getPopupContainer={getBodyPopupContainer}
            >
              <button type="button" className={styles.deleteBtn}>
                <DeleteOutlined />
                Delete
              </button>
            </Popconfirm>
          )
        ) : (
          <span />
        )}
        <Button type="primary" onClick={handleSave} className={styles.saveBtn}>
          Save
        </Button>
      </div>
    </div>
  );
};
